import React from 'react';
import { Alert, Flex } from 'antd';
import { faCircle, faCircleDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';

import { MSTeamsStatus } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { CircledFontAwesomeIcon } from 'jf/components/CircledFontAwesomeIcon';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExLink } from 'jf/components/DevExLink';
import { DevExText } from 'jf/components/DevExText';
import { ReleaseTag } from 'jf/components/ReleaseTag';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { useClientFlags } from 'jf/utils/useClientFlags';

import { useStudyBuilderState } from '../StudyBuilderState';
import { useStudyUpdater } from '../useStudyUpdater';

import { MSTeamsTargetEditor } from './msteams/MSTeamsTargetEditor';
import { IntegrationSetup } from './IntegrationSetup';
import { SlackTargetEditor } from './SlackTargetEditor';

export const TargetingCard = ({
  messengerStatus,
  isIntegrationError,
}: {
  messengerStatus: MSTeamsStatus | undefined;
  isIntegrationError: boolean;
}) => {
  const { msgProvider, msgProviderFull, msgIcon } = useTargetingClient();
  const state = useStudyBuilderState();
  const theme = useDevExTheme();
  const { msTeamsTargeting } = useClientFlags();
  const studyUpdater = useStudyUpdater(state.studyRef);

  return (
    <DevExCard
      onClick={
        !state.slackTargetingEnabled
          ? () => {
              const newdate = dayjs().add(2, 'week').startOf('day').toISOString();
              state.update({
                slackTargetingEnabled: true,
                scheduledCloseDate: newdate,
                enableScheduledReminders: true,
              });
              studyUpdater.update({
                scheduledCloseDate: newdate,
                enableScheduledReminders: true,
              });
            }
          : undefined
      }
      title={
        <>
          <FontAwesomeIcon
            icon={state.slackTargetingEnabled ? faCircleDot : faCircle}
            color={theme.color.brand.active}
          />
          Distribute in {msgProvider}
        </>
      }
      extra={<ReleaseTag stage={msTeamsTargeting ? 'ALPHA' : 'BETA'} />}
    >
      {state.slackTargetingEnabled ? (
        !messengerStatus?.connected ? (
          <Flex justify="center">
            <Flex vertical align="center" gap={theme.variable.spacing.lg}>
              {!isIntegrationError && (
                <CircledFontAwesomeIcon
                  icon={msgIcon}
                  style={{ color: theme.color.text.tertiary }}
                />
              )}
              <Flex
                vertical
                gap={theme.variable.spacing.lg}
                style={{ paddingInline: theme.variable.spacing.lg, textAlign: 'left' }}
              >
                {isIntegrationError ? (
                  <Alert
                    message={
                      <div
                        style={{
                          marginLeft: `${theme.variable.spacing.sm}`,
                          marginRight: `${theme.variable.spacing.lg}`,
                        }}
                      >
                        {`There was an error retrieving your ${msgProvider} installation details. Please try again later, or choose a different distribution method.`}
                      </div>
                    }
                    showIcon
                    action={
                      <DevExLink onClick={() => window.location.reload()}>Try again</DevExLink>
                    }
                    type="error"
                  />
                ) : (
                  <IntegrationSetup />
                )}
              </Flex>
            </Flex>
          </Flex>
        ) : (
          <>
            <DevExText type="secondary" fontSize="md">
              Select this option to send the survey as a {msgProviderFull} direct message to each
              recipient.
            </DevExText>
            {msTeamsTargeting ? <MSTeamsTargetEditor /> : <SlackTargetEditor />}
          </>
        )
      ) : (
        <DevExText type="secondary" fontSize="md" lined>
          Our {msgProvider} bot handles your survey distribution seamlessly. Key benefits:
          <ul style={{ margin: 0 }}>
            <li>Ensures an accurate response rate</li>
            <li>Sends reminders only to those who haven't responded yet</li>
          </ul>
        </DevExText>
      )}
    </DevExCard>
  );
};
